import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Search, MapPin, Users, Building2, Briefcase } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const COMPANIES_PER_PAGE = 20;

interface Company {
  id: string;
  company_name: string;
  sector: string | null;
  city: string | null;
  state: string | null;
  employee_count: string | null;
  logo_url: string | null;
  openPositions: number;
}

export default function EmpresasPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSector, setSelectedSector] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.ceil(totalCount / COMPANIES_PER_PAGE);

  const sectors = ["Tecnologia", "Indústria", "Varejo", "Serviços", "Logística", "Saúde", "Educação", "Financeiro"];

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    
    const from = (currentPage - 1) * COMPANIES_PER_PAGE;
    const to = from + COMPANIES_PER_PAGE - 1;

    let queryBuilder = supabase
      .from("company_profiles")
      .select("id, company_name, sector, city, state, employee_count, logo_url", { count: 'exact' })
      .eq("is_verified", true);

    // Aplicar filtro de busca
    if (searchTerm) {
      queryBuilder = queryBuilder.or(
        `company_name.ilike.%${searchTerm}%,sector.ilike.%${searchTerm}%`
      );
    }

    // Aplicar filtro de setor
    if (selectedSector) {
      queryBuilder = queryBuilder.eq("sector", selectedSector);
    }

    const { data: companiesData, error, count } = await queryBuilder
      .order("company_name", { ascending: true })
      .range(from, to);

    if (error || !companiesData) {
      setLoading(false);
      return;
    }

    setTotalCount(count || 0);

    // Fetch job counts para as empresas da pagina
    const companiesWithJobs = await Promise.all(
      companiesData.map(async (company) => {
        const { count } = await supabase
          .from("jobs")
          .select("id", { count: "exact", head: true })
          .eq("company_id", company.id)
          .eq("is_active", true);
        return {
          ...company,
          openPositions: count || 0,
        };
      })
    );

    setCompanies(companiesWithJobs);
    setLoading(false);
  }, [currentPage, searchTerm, selectedSector]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSectorChange = (sector: string | null) => {
    setSelectedSector(sector);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const generatePageNumbers = (): (number | "ellipsis")[] => {
    const pages: (number | "ellipsis")[] = [];
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push("ellipsis");
      }
      
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push("ellipsis");
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 via-background to-accent/10 py-12 md:py-16">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
                Empresas que Contratam na <span className="text-accent">JobWay</span><span className="text-primary">X</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Conheça as empresas parceiras e encontre sua próxima oportunidade
              </p>
              
              {/* Search */}
              <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input 
                  placeholder="Buscar empresa ou setor..." 
                  className="pl-12 h-12 text-base" 
                  value={searchTerm} 
                  onChange={e => handleSearchChange(e.target.value)} 
                />
              </div>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="border-b border-border bg-card/50">
          <div className="container py-4">
            <div className="flex flex-wrap gap-2">
              <Button 
                variant={selectedSector === null ? "default" : "outline"} 
                size="sm" 
                onClick={() => handleSectorChange(null)}
              >
                Todos
              </Button>
              {sectors.map(sector => (
                <Button 
                  key={sector} 
                  variant={selectedSector === sector ? "default" : "outline"} 
                  size="sm" 
                  onClick={() => handleSectorChange(sector)}
                >
                  {sector}
                </Button>
              ))}
            </div>
          </div>
        </section>

        {/* Companies Grid */}
        <section className="py-12">
          <div className="container">
            <div className="mobile-container md:w-full">
              <div className="flex items-center justify-between mb-8">
                <p className="text-muted-foreground">
                  {loading 
                    ? "Carregando..." 
                    : totalCount === 0
                      ? "Nenhuma empresa encontrada"
                      : `Mostrando ${((currentPage - 1) * COMPANIES_PER_PAGE) + 1}-${Math.min(currentPage * COMPANIES_PER_PAGE, totalCount)} de ${totalCount} empresa${totalCount !== 1 ? "s" : ""}`
                  }
                </p>
              </div>

              {loading ? (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <Card key={i}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <Skeleton className="h-14 w-14 rounded-xl" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-5 w-32" />
                            <Skeleton className="h-4 w-24" />
                          </div>
                        </div>
                        <div className="mt-4 space-y-2">
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                        <Skeleton className="h-10 w-full mt-4" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {companies.map(company => (
                    <Card key={company.id} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                      <CardContent className="p-4 md:p-6">
                        <div className="flex items-start gap-3 md:gap-4">
                          <div className="flex h-10 w-10 md:h-14 md:w-14 items-center justify-center rounded-lg md:rounded-xl bg-primary/10 text-primary font-bold text-base md:text-xl shrink-0">
                            {company.company_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-foreground text-sm md:text-lg truncate group-hover:text-primary transition-colors">
                              {company.company_name}
                            </h3>
                            <p className="text-xs md:text-sm text-muted-foreground truncate">
                              {company.sector || "Não informado"}
                            </p>
                          </div>
                        </div>

                        <div className="mt-3 md:mt-4 space-y-1.5 md:space-y-2">
                          <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0" />
                            <span className="truncate">
                              {company.city || company.state 
                                ? `${company.city || ""}${company.city && company.state ? ", " : ""}${company.state || ""}` 
                                : "Não informado"}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-muted-foreground">
                            <Users className="h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0" />
                            <span className="truncate">{company.employee_count || "Não informado"}</span>
                          </div>
                          <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-primary font-medium">
                            <Briefcase className="h-3.5 w-3.5 md:h-4 md:w-4 flex-shrink-0" />
                            <span>{company.openPositions} vagas abertas</span>
                          </div>
                        </div>

                        <Button className="w-full mt-3 md:mt-4" size="sm" variant="outline" asChild>
                          <Link to={`/empresas/${company.id}/vagas`}>
                            Ver Vagas
                          </Link>
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {!loading && companies.length === 0 && (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">Nenhuma empresa encontrada</h3>
                  <p className="text-muted-foreground">Tente ajustar os filtros de busca</p>
                </div>
              )}

              {totalCount > 0 && (
                <Pagination className="mt-8">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    
                    {generatePageNumbers().map((pageNum, idx) => (
                      pageNum === "ellipsis" ? (
                        <PaginationItem key={`ellipsis-${idx}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      ) : (
                        <PaginationItem key={pageNum}>
                          <PaginationLink
                            onClick={() => handlePageChange(pageNum)}
                            isActive={currentPage === pageNum}
                            className="cursor-pointer"
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      )
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                        className={currentPage === totalPages || totalPages === 0 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
